import type { ColDef, ColumnState, GridApi } from 'ag-grid-community';
import { camelize } from 'helpers/syntaxHelper';

export const isNotANumber = (value: unknown): boolean =>
    typeof value !== 'number' || Number.isNaN(value);
export const isANumber = (value: unknown): value is number =>
    !isNotANumber(value);

export const isFloatParsable = (value: unknown): boolean =>
    typeof value === 'number' ||
    (typeof value === 'string' && /^[0-9.]+$/.test(value));

export const getFiltersFromTCDNode = (
    pivotKeys: string[],
    node: FlowAnyObject,
    gridApi: GridApi,
): QueryFilterT[] => {
    const pivotColumns = gridApi.getPivotColumns();

    const filters = [];

    let currentNode = node;
    while (currentNode) {
        // I don't have a better way,trying something new here
        if (currentNode.field && currentNode.key !== undefined) {
            const { groupValue } = currentNode;
            // // When groupValue is an object, the grid is grouping by service, so use its value instead of its label.
            const value =
                groupValue !== null &&
                typeof groupValue === 'object' &&
                'value' in groupValue
                    ? groupValue.value
                    : currentNode.key;
            filters.push({
                field: currentNode.field,
                operation: 'equal',
                value: value,
            });
        }

        currentNode = currentNode?.parent;
    }

    pivotKeys.forEach((pivotKey, i) => {
        if (pivotKey && i <= pivotColumns.length - 1) {
            filters.push({
                field: pivotColumns[i].getColId(),
                operation: 'equal',
                value: pivotKey,
            });
        }
        if (i > pivotColumns.length - 1) {
            console.error(
                'unexpected behavior the length of pivotKeys is higher than the length of pivotColumns',
            );
        }
    });
    // @ts-expect-error [TS migration] Was not detected by flow
    return filters;
};

export const getFilterFromClick = (
    clickedColumn: string,
    gridApi: GridApi,
    allFiltersFromNode: QueryFilterT[],
): QueryFilterT[] => {
    const displayedColumns = gridApi
        .getAllDisplayedColumns()
        .map((col) => col.getColId());
    const columnsToFilter = displayedColumns.filter(
        (col) =>
            displayedColumns.indexOf(col) <=
            displayedColumns.indexOf(clickedColumn),
    );
    const pivotColumns = gridApi.getPivotColumns().map((col) => col.getColId());
    const getColumnId = (col: string): string => `ag-Grid-AutoColumn-${col}`;
    return allFiltersFromNode.filter((filter) => {
        const camelizedField = camelize(filter.field);
        return (
            columnsToFilter.includes(getColumnId(camelizedField)) ||
            pivotColumns.includes(camelizedField)
        );
    });
};

/* Wrapper around Aggrid's applyColumnState.
 * Do not use columnApi.applyColumnState directly, but this function instead.
 */
export const getApplyColumnState = (gridApi: GridApi | null) => {
    const applyColumnState = (currentColumnState: ColDef[]) => {
        if (gridApi === null) {
            return;
        }
        gridApi.applyColumnState({
            state: currentColumnState.map((c) => {
                const colId =
                    c.colId ||
                    (c as { field?: string }).field ||
                    (c as { key?: string }).key ||
                    '';

                const { sort, ...restOfColDef } = c;

                const state: ColumnState = {
                    ...(restOfColDef as unknown as ColumnState),
                    colId,
                    sort:
                        typeof sort === 'string'
                            ? (sort as 'asc' | 'desc')
                            : undefined,
                };

                if (state.width !== null && state.width !== undefined) {
                    state.flex = null;
                }
                return state;
            }),
            applyOrder: true,
        });
    };
    return applyColumnState;
};
